import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const IntelligenceContext = createContext(null);

const STORAGE_KEYS = {
  SAVED_VIEWS: 'lysarq_msme_saved_views',
  RECENT_SEARCHES: 'lysarq_msme_recent_searches',
  WATCHLIST: 'lysarq_msme_watchlist',
  COMPARISON: 'lysarq_msme_comparison',
  USER_PROFILE: 'lysarq_msme_user_profile',
  NOTIFICATIONS: 'lysarq_msme_notifications',
  DENSITY: 'lysarq_msme_density',
};

export const DEFAULT_BUSINESS_PROFILE = {
  businessName: '',
  contactPerson: '',
  role: 'Owner / Founder',
  email: '',
  enterpriseScale: 'Micro', // 'Micro' | 'Small' | 'Medium'
  sectorCode: '', // NIC Division
  sectorName: '',
  district: 'coimbatore',
  pincode: '',
  yearEstablished: '',
  memberTier: 'Lysarq MSME · Local Analytics Workspace',
  annualTurnover: '',
};

export const DEFAULT_USER_PROFILE = {
  name: '',
  role: 'Owner / Founder',
  organization: '',
  email: '',
  memberTier: 'Lysarq MSME · Local Analytics Workspace',
  memberSince: '',
};

export const DEFAULT_NOTIFICATIONS = {
  marketChanges: {
    enabled: true,
    thresholdPct: 15,
    direction: 'above',
    frequency: 'realtime',
  },
  systemAlerts: {
    enabled: true,
    sensitivity: 'high',
    densityThreshold: 60,
  },
  watchlistAlerts: {
    enabled: true,
    notifyOnNewUnits: true,
    notifyOnTrendShift: true,
  },
  forecastUpdates: {
    enabled: true,
    cadence: 'refresh',
    minConfidence: 90,
  },
  weeklyDigest: false,
};

// Normalizer to ensure legacy boolean configs are seamlessly migrated to structured objects
function normalizeNotifications(saved) {
  if (!saved || typeof saved !== 'object') return DEFAULT_NOTIFICATIONS;
  return {
    marketChanges: typeof saved.marketChanges === 'boolean'
      ? { ...DEFAULT_NOTIFICATIONS.marketChanges, enabled: saved.marketChanges }
      : { ...DEFAULT_NOTIFICATIONS.marketChanges, ...(saved.marketChanges || {}) },
    systemAlerts: typeof saved.systemAlerts === 'boolean'
      ? { ...DEFAULT_NOTIFICATIONS.systemAlerts, enabled: saved.systemAlerts }
      : { ...DEFAULT_NOTIFICATIONS.systemAlerts, ...(saved.systemAlerts || {}) },
    watchlistAlerts: typeof saved.watchlistAlerts === 'boolean'
      ? { ...DEFAULT_NOTIFICATIONS.watchlistAlerts, enabled: saved.watchlistAlerts }
      : { ...DEFAULT_NOTIFICATIONS.watchlistAlerts, ...(saved.watchlistAlerts || {}) },
    forecastUpdates: typeof saved.forecastUpdates === 'boolean'
      ? { ...DEFAULT_NOTIFICATIONS.forecastUpdates, enabled: saved.forecastUpdates }
      : { ...DEFAULT_NOTIFICATIONS.forecastUpdates, ...(saved.forecastUpdates || {}) },
    weeklyDigest: typeof saved.weeklyDigest === 'boolean' ? saved.weeklyDigest : false,
  };
}

const DEFAULT_ALERTS_FEED = [
  {
    id: 'alt-1',
    type: 'marketChanges',
    title: 'Coimbatore Market Velocity Alert',
    description: 'Fabricated Metal Products (NIC 25) registration trajectory reached +16.2% yearly growth, surpassing your +15% alert threshold.',
    urgency: 'high',
    timestamp: 'Just now',
    read: false,
    targetTab: 'trends',
    targetParams: { district: 'coimbatore', sector: '25' },
  },
  {
    id: 'alt-2',
    type: 'systemAlerts',
    title: 'Supply Density Spike · PIN 641402',
    description: 'Active peer concentration index reached 82nd percentile with 175 competitor units registered.',
    urgency: 'moderate',
    timestamp: '25 mins ago',
    read: false,
    targetTab: 'search',
    targetParams: { pincode: '641402', sector: '25' },
  },
  {
    id: 'alt-3',
    type: 'forecastUpdates',
    title: 'Regional MSME Outlook Refreshed',
    description: 'Quarterly predictive horizon updated with verified registry records and positive 6-month outlook.',
    urgency: 'opportunity',
    timestamp: '2 hours ago',
    read: true,
    targetTab: 'forecast',
    targetParams: {},
  }
];

export const DEFAULT_DENSITY = 'comfortable';

export function IntelligenceProvider({ children }) {
  // Global Filters
  // const [district, setDistrict] = useState('all');
  const [district, setDistrict] = useState('tamilnadu');
  const [selectedSector, setSelectedSector] = useState(null);
  const [userPincode, setUserPincode] = useState(null);
  const [timeframe, setTimeframe] = useState('1Y'); // '1Y' | '3Y' | '5Y' | 'ALL'

  // Business Profile (Persistent in local browser localStorage only)
  const [businessProfile, setBusinessProfileState] = useState(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
      if (item) {
        const parsed = JSON.parse(item);
        // Automatically migrate legacy sample dummy profile out of visitor's localStorage
        if (parsed?.contactPerson === 'Aravind Krishnan' || parsed?.businessName === 'Precision Engineering & Tooling') {
          localStorage.removeItem(STORAGE_KEYS.USER_PROFILE);
          return DEFAULT_BUSINESS_PROFILE;
        }
        return { ...DEFAULT_BUSINESS_PROFILE, ...parsed };
      }
      return DEFAULT_BUSINESS_PROFILE;
    } catch {
      return DEFAULT_BUSINESS_PROFILE;
    }
  });

  const setBusinessProfile = useCallback((profileOrFn) => {
    setBusinessProfileState((prev) => {
      const next = typeof profileOrFn === 'function' ? profileOrFn(prev) : profileOrFn;
      try { localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const [notifications, setNotificationsState] = useState(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEYS.NOTIFICATIONS);
      return item ? normalizeNotifications(JSON.parse(item)) : DEFAULT_NOTIFICATIONS;
    } catch {
      return DEFAULT_NOTIFICATIONS;
    }
  });

  const setNotifications = useCallback((notifsOrFn) => {
    setNotificationsState((prev) => {
      const raw = typeof notifsOrFn === 'function' ? notifsOrFn(prev) : notifsOrFn;
      const next = normalizeNotifications(raw);
      try { localStorage.setItem(STORAGE_KEYS.NOTIFICATIONS, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // Live Alerts Feed & Notification Center
  const [alertsFeed, setAlertsFeed] = useState(() => {
    try {
      const saved = localStorage.getItem('lysarq_msme_alerts_feed');
      return saved ? JSON.parse(saved) : DEFAULT_ALERTS_FEED;
    } catch {
      return DEFAULT_ALERTS_FEED;
    }
  });

  const [activeToast, setActiveToast] = useState(null);

  const triggerTestAlert = useCallback((type) => {
    const alertTypes = {
      marketChanges: {
        title: `Market Velocity Trigger: ${district.toUpperCase()}`,
        description: `Sector registration growth exceeded ${notifications.marketChanges?.thresholdPct || 15}% annual growth threshold in ${district.toUpperCase()}.`,
        urgency: 'high',
        targetTab: 'trends',
        targetParams: { district, sector: selectedSector || '25' },
      },
      systemAlerts: {
        title: `Saturation Alert: PIN ${userPincode || '641402'}`,
        description: `Competitor density in PIN ${userPincode || '641402'} is currently categorized as ${notifications.systemAlerts?.sensitivity === 'high' ? 'High Supply Saturation' : 'Elevated Density'}.`,
        urgency: 'moderate',
        targetTab: 'search',
        targetParams: { pincode: userPincode || '641402', sector: selectedSector || '25' },
      },
      watchlistAlerts: {
        title: `Watchlist Update: ${district.toUpperCase()}`,
        description: `New active registry filings detected in monitored sector classifications.`,
        urgency: 'opportunity',
        targetTab: 'search',
        targetParams: { district, sector: selectedSector || '25' },
      },
      forecastUpdates: {
        title: `Outlook Sync: 99.9% Assurity SLA`,
        description: `Updated multi-horizon projection generated for NIC ${selectedSector || '25'} with 6-month positive trajectory.`,
        urgency: 'opportunity',
        targetTab: 'forecast',
        targetParams: { pincode: userPincode || '641402', sector: selectedSector || '25', district },
      },
    };

    const config = alertTypes[type] || alertTypes.marketChanges;
    const newAlert = {
      id: `alt-${Date.now()}`,
      type,
      title: config.title,
      description: config.description,
      urgency: config.urgency,
      timestamp: 'Just now',
      read: false,
      targetTab: config.targetTab,
      targetParams: config.targetParams,
    };

    setAlertsFeed((prev) => {
      const next = [newAlert, ...prev.slice(0, 15)];
      try { localStorage.setItem('lysarq_msme_alerts_feed', JSON.stringify(next)); } catch {}
      return next;
    });

    // Fire toast
    setActiveToast(newAlert);
    setTimeout(() => {
      setActiveToast((curr) => (curr?.id === newAlert.id ? null : curr));
    }, 4500);
  }, [district, selectedSector, userPincode, notifications]);

  const markAlertAsRead = useCallback((id) => {
    setAlertsFeed((prev) => {
      const next = prev.map((a) => (a.id === id ? { ...a, read: true } : a));
      try { localStorage.setItem('lysarq_msme_alerts_feed', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const markAllAlertsAsRead = useCallback(() => {
    setAlertsFeed((prev) => {
      const next = prev.map((a) => ({ ...a, read: true }));
      try { localStorage.setItem('lysarq_msme_alerts_feed', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const dismissAlert = useCallback((id) => {
    setAlertsFeed((prev) => {
      const next = prev.filter((a) => a.id !== id);
      try { localStorage.setItem('lysarq_msme_alerts_feed', JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  const unreadAlertCount = alertsFeed.filter((a) => !a.read).length;

  const [density, setDensityState] = useState(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEYS.DENSITY);
      return item || DEFAULT_DENSITY;
    } catch {
      return DEFAULT_DENSITY;
    }
  });

  const setDensity = useCallback((val) => {
    setDensityState(val);
    try { localStorage.setItem(STORAGE_KEYS.DENSITY, val); } catch {}
  }, []);

  useEffect(() => {
    try {
      document.documentElement.setAttribute('data-density', density || 'comfortable');
      if (density === 'compact') {
        document.body.classList.add('density-compact');
      } else {
        document.body.classList.remove('density-compact');
      }
    } catch (e) {
      console.debug('Density sync error:', e);
    }
  }, [density]);

  const resetProfileDefaults = useCallback(() => {
    setBusinessProfile(DEFAULT_BUSINESS_PROFILE);
    setNotifications(DEFAULT_NOTIFICATIONS);
    setDensity(DEFAULT_DENSITY);
  }, [setBusinessProfile, setNotifications, setDensity]);

  // Network / Offline Status
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Saved Views (localStorage)
  const [savedViews, setSavedViews] = useState(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEYS.SAVED_VIEWS);
      return item ? JSON.parse(item) : [
        { id: 'view-1', name: 'Coimbatore Textile & Apparel Hub', district: 'coimbatore', sector: 'C', pincode: '641001', createdAt: new Date().toISOString() },
        // { id: 'view-2', name: 'Tamil Nadu Precision Engineering', district: 'all', sector: 'C', pincode: '641402', createdAt: new Date().toISOString() }
        { id: 'view-2', name: 'Tamil Nadu Precision Engineering', district: 'tamilnadu', sector: 'C', pincode: '641402', createdAt: new Date().toISOString() }
      ];
    } catch {
      return [];
    }
  });

  const saveCurrentView = useCallback((name) => {
    const newView = {
      id: `view-${Date.now()}`,
      // name: name || `${district === 'all' ? 'India' : district === 'tamilnadu' ? 'Tamil Nadu' : district.charAt(0).toUpperCase() + district.slice(1)} Market View`,
      name: name || `${(district === 'tamilnadu' || district === 'all') ? 'Tamil Nadu' : district.charAt(0).toUpperCase() + district.slice(1)} Market View`,
      district,
      sector: selectedSector,
      pincode: userPincode,
      createdAt: new Date().toISOString(),
    };
    setSavedViews((prev) => {
      const updated = [newView, ...prev.slice(0, 9)];
      try { localStorage.setItem(STORAGE_KEYS.SAVED_VIEWS, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, [district, selectedSector, userPincode]);

  const deleteSavedView = useCallback((id) => {
    setSavedViews((prev) => {
      const updated = prev.filter((v) => v.id !== id);
      try { localStorage.setItem(STORAGE_KEYS.SAVED_VIEWS, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  const applySavedView = useCallback((view) => {
    if (!view) return;
    if (view.district) setDistrict(view.district);
    if (view.sector) setSelectedSector(view.sector);
    if (view.pincode) setUserPincode(view.pincode);
  }, []);

  // Recent Searches & Views (localStorage)
  const [recentSearches, setRecentSearches] = useState(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEYS.RECENT_SEARCHES);
      return item ? JSON.parse(item) : ['Coimbatore', 'Textile', '641402', 'Machinery'];
    } catch {
      return [];
    }
  });

  const addRecentSearch = useCallback((term) => {
    if (!term || typeof term !== 'string') return;
    const clean = term.trim();
    if (!clean) return;
    setRecentSearches((prev) => {
      const filtered = prev.filter((t) => t.toLowerCase() !== clean.toLowerCase());
      const updated = [clean, ...filtered].slice(0, 8);
      try { localStorage.setItem(STORAGE_KEYS.RECENT_SEARCHES, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  // Watchlist (localStorage)
  const [watchlist, setWatchlist] = useState(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEYS.WATCHLIST);
      return item ? JSON.parse(item) : [
        { id: 'wl-1', type: 'district', code: 'coimbatore', name: 'Coimbatore District', metric: '+18.4% yearly' },
        { id: 'wl-2', type: 'sector', code: '13', name: 'Manufacture of Textiles', metric: '+21.3% yearly' }
      ];
    } catch {
      return [];
    }
  });

  const toggleWatchlist = useCallback((item) => {
    if (!item?.id) return;
    setWatchlist((prev) => {
      const exists = prev.some((w) => w.id === item.id);
      const updated = exists ? prev.filter((w) => w.id !== item.id) : [item, ...prev];
      try { localStorage.setItem(STORAGE_KEYS.WATCHLIST, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  const isWatchlisted = useCallback((id) => {
    return watchlist.some((w) => w.id === id);
  }, [watchlist]);

  // Comparison Workspace (Persistent across navigation)
  const [comparisonEntities, setComparisonEntities] = useState(() => {
    try {
      const item = localStorage.getItem(STORAGE_KEYS.COMPARISON);
      return item ? JSON.parse(item) : { userMSME: null, competitors: [null, null, null] };
    } catch {
      return { userMSME: null, competitors: [null, null, null] };
    }
  });

  const setComparisonUserMSME = useCallback((biz) => {
    setComparisonEntities((prev) => {
      const updated = { ...prev, userMSME: biz };
      try { localStorage.setItem(STORAGE_KEYS.COMPARISON, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  const setComparisonCompetitor = useCallback((index, biz) => {
    setComparisonEntities((prev) => {
      const newCompetitors = [...prev.competitors];
      newCompetitors[index] = biz;
      const updated = { ...prev, competitors: newCompetitors };
      try { localStorage.setItem(STORAGE_KEYS.COMPARISON, JSON.stringify(updated)); } catch {}
      return updated;
    });
  }, []);

  const clearComparison = useCallback(() => {
    const reset = { userMSME: null, competitors: [null, null, null] };
    setComparisonEntities(reset);
    try { localStorage.setItem(STORAGE_KEYS.COMPARISON, JSON.stringify(reset)); } catch {}
  }, []);

  // "Why?" Factor Explanation Modal State
  const [whyModalData, setWhyModalData] = useState(null); // { title, subtitle, score, factors: [], takeaway }

  const openWhyModal = useCallback((data) => {
    setWhyModalData(data);
  }, []);

  const closeWhyModal = useCallback(() => {
    setWhyModalData(null);
  }, []);

  // Universal In-Web Modal State ('preset' | 'export' | 'share' | null)
  const [activeModal, setActiveModal] = useState(null);

  const openModal = useCallback((type) => {
    setActiveModal(type);
  }, []);

  const closeModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const value = {
    district,
    setDistrict,
    selectedSector,
    setSelectedSector,
    userPincode,
    setUserPincode,
    timeframe,
    setTimeframe,
    isOffline,
    savedViews,
    saveCurrentView,
    deleteSavedView,
    applySavedView,
    recentSearches,
    addRecentSearch,
    watchlist,
    toggleWatchlist,
    isWatchlisted,
    comparisonEntities,
    setComparisonUserMSME,
    setComparisonCompetitor,
    clearComparison,
    whyModalData,
    openWhyModal,
    closeWhyModal,
    activeModal,
    openModal,
    closeModal,
    businessProfile,
    setBusinessProfile,
    userProfile: businessProfile,
    setUserProfile: setBusinessProfile,
    notifications,
    setNotifications,
    alertsFeed,
    unreadAlertCount,
    markAlertAsRead,
    markAllAlertsAsRead,
    dismissAlert,
    triggerTestAlert,
    activeToast,
    setActiveToast,
    density,
    setDensity,
    resetProfileDefaults,
  };

  return (
    <IntelligenceContext.Provider value={value}>
      {children}
    </IntelligenceContext.Provider>
  );
}

export function useIntelligence() {
  const context = useContext(IntelligenceContext);
  if (!context) {
    throw new Error('useIntelligence must be used within an IntelligenceProvider');
  }
  return context;
}
